import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth';

export interface SubscriptionContext {
  subscriberId: string | null;
  companyName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionContextService {
  private selectedSubscriberSubject = new BehaviorSubject<SubscriptionContext>({
    subscriberId: null
  });

  public selectedSubscriber$: Observable<SubscriptionContext> = this.selectedSubscriberSubject.asObservable();

  constructor(private authService: AuthService) {
    // Initialize from localStorage if available
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    // Check localStorage first
    const storedSubscriberId = localStorage.getItem('selectedSubscriberId');
    if (storedSubscriberId) {
      this.selectedSubscriberSubject.next({
        subscriberId: storedSubscriberId
      });
      return;
    }

    // If no stored value, try to get from current user
    try {
      const currentUser = this.authService.getLoggedInUser();
      console.log('SubscriptionContextService: Current user:', currentUser);
      console.log('SubscriptionContextService: Current user subscriberId:', currentUser?.subscriberId?._id);
      if (currentUser?.subscriberId?._id) {
        this.selectedSubscriberSubject.next({
          subscriberId: currentUser.subscriberId._id
        });
      }
    } catch (error) {
      console.warn('SubscriptionContextService: Could not access AuthService during initialization:', error);
    }
  }

  setSelectedSubscriber(subscriberId: string | null, companyName?: string): void {
    if (subscriberId) {
      localStorage.setItem('selectedSubscriberId', subscriberId);
      this.selectedSubscriberSubject.next({
        subscriberId,
        companyName
      });
    } else {
      localStorage.removeItem('selectedSubscriberId');
      this.selectedSubscriberSubject.next({
        subscriberId: null
      });
    }
  }

  getSelectedSubscriberId(): string | null {
    return this.selectedSubscriberSubject.value.subscriberId;
  }

  getSelectedSubscriber(): SubscriptionContext {
    return this.selectedSubscriberSubject.value;
  }

  clearSelectedSubscriber(): void {
    this.setSelectedSubscriber(null);
  }

  isSubscriberSelected(): boolean {
    return !!this.selectedSubscriberSubject.value.subscriberId;
  }

  /**
   * Refresh subscription context from current user
   * This method can be called after login to update the subscription context
   */
  refreshFromCurrentUser(): void {
    try {
      const currentUser = this.authService.getLoggedInUser();
      console.log('SubscriptionContextService: Refreshing from current user:', currentUser);
      
      if (currentUser?.subscriberId?._id) {
        this.selectedSubscriberSubject.next({
          subscriberId: currentUser.subscriberId._id
        });
        // Also store in localStorage for persistence
        localStorage.setItem('selectedSubscriberId', currentUser.subscriberId._id);
      }
    } catch (error) {
      console.warn('SubscriptionContextService: Could not refresh from current user:', error);
    }
  }
}
